import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Water Damage",
  "Fire & Smoke",
  "Mold Remediation",
  "Storm & Disaster Cleanup",
  "Free Roof & Siding Inspection",
  "Not Sure / Other",
];

const empty = { name: "", phone: "", email: "", service: "", address: "", message: "" };

export default function QuoteForm() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service) {
      toast.error("Please fill in your name, phone, email and service.");
      return;
    }
    if (!consent) {
      toast.error("Please agree to receive text messages to continue.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Request received. Our team will call you shortly.");
      setForm(empty);
      setConsent(false);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-[#0d0d0d] border border-white/10 px-4 py-3.5 text-[#F5F5F0] placeholder:text-[#5a5a5a] focus:outline-none focus:border-gold transition-colors duration-300";

  return (
    <section id="quote" className="relative py-24 lg:py-32 bg-[#0a0a0a] border-t border-white/10" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-16">
        <div className="lg:col-span-2">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-gold">Request Service</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
            Get help<br /><span className="text-gradient-gold">right now.</span>
          </h2>
          <p className="mt-6 text-[#A3A3A3] leading-relaxed max-w-sm">
            Emergency? Call us directly for the fastest response. Otherwise, send your details and we'll reach out immediately.
          </p>

          <div className="mt-10 space-y-6">
            <a href={CONTACT.phoneHref} className="flex items-center gap-4 group" data-testid="quote-phone">
              <span className="flex h-11 w-11 items-center justify-center border border-gold/40 text-gold group-hover:bg-gold group-hover:text-black transition-colors duration-300"><Phone className="h-5 w-5" /></span>
              <span className="font-display text-xl group-hover:text-gold transition-colors">{CONTACT.phone}</span>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group" data-testid="quote-email">
              <span className="flex h-11 w-11 items-center justify-center border border-gold/40 text-gold group-hover:bg-gold group-hover:text-black transition-colors duration-300"><Mail className="h-5 w-5" /></span>
              <span className="text-[#F5F5F0] group-hover:text-gold transition-colors">{CONTACT.email}</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center border border-gold/40 text-gold"><MapPin className="h-5 w-5" /></span>
              <span className="text-[#A3A3A3]">{CONTACT.address}</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="h-full min-h-[420px] flex flex-col items-center justify-center text-center border border-gold/30 bg-[#0d0d0d] p-12"
              data-testid="quote-success"
            >
              <CheckCircle2 className="h-16 w-16 text-gold" />
              <h3 className="font-display text-3xl mt-6">Request Received</h3>
              <p className="text-[#A3A3A3] mt-4 max-w-sm">Thank you. A restoration specialist will contact you shortly. For emergencies, call {CONTACT.phone}.</p>
              <button onClick={() => setDone(false)} className="mt-8 border border-gold text-gold px-6 py-3 hover:bg-gold hover:text-black transition-colors" data-testid="quote-reset-btn">
                Submit Another Request
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-[#0d0d0d] p-8 lg:p-10 space-y-5" data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono text-[11px] tracking-widest uppercase text-[#A3A3A3]">Full Name *</label>
                  <input className={`${inputClass} mt-2`} value={form.name} onChange={update("name")} placeholder="Jane Doe" data-testid="quote-input-name" />
                </div>
                <div>
                  <label className="font-mono text-[11px] tracking-widest uppercase text-[#A3A3A3]">Phone *</label>
                  <input className={`${inputClass} mt-2`} value={form.phone} onChange={update("phone")} placeholder="(708) 000-0000" data-testid="quote-input-phone" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono text-[11px] tracking-widest uppercase text-[#A3A3A3]">Email *</label>
                  <input type="email" className={`${inputClass} mt-2`} value={form.email} onChange={update("email")} placeholder="you@email.com" data-testid="quote-input-email" />
                </div>
                <div>
                  <label className="font-mono text-[11px] tracking-widest uppercase text-[#A3A3A3]">Service Needed *</label>
                  <select className={`${inputClass} mt-2 appearance-none cursor-pointer`} value={form.service} onChange={update("service")} data-testid="quote-select-service">
                    <option value="" disabled>Select a service</option>
                    {SERVICE_OPTIONS.map((s) => <option key={s} value={s} className="bg-[#0d0d0d]">{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-widest uppercase text-[#A3A3A3]">Property Address</label>
                <input className={`${inputClass} mt-2`} value={form.address} onChange={update("address")} placeholder="Street, City, State" data-testid="quote-input-address" />
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-widest uppercase text-[#A3A3A3]">Describe the Damage</label>
                <textarea rows={4} className={`${inputClass} mt-2 resize-none`} value={form.message} onChange={update("message")} placeholder="Tell us what happened..." data-testid="quote-input-message" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="quote-consent-label">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37] cursor-pointer" data-testid="quote-consent-checkbox" />
                <span className="text-xs text-[#8a8a8a] leading-relaxed" data-testid="quote-sms-consent">
                  I agree to receive SMS text messages from 247 Aftermath Restoration Mgmt about my service request and appointments at the number provided. Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out, HELP for help. See our <a href="/privacy" className="text-gold underline hover:text-gold-light">Privacy Policy</a> &amp; <a href="/terms" className="text-gold underline hover:text-gold-light">Terms</a>.
                </span>
              </label>
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 bg-gold text-black font-semibold px-8 py-4 hover:bg-gold-light transition-colors duration-300 disabled:opacity-60"
                data-testid="quote-submit-btn"
              >
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</> : <><Send className="h-5 w-5" /> Request My Free Assessment</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
